// @flow
// ----------------------------------------------------------------------
// <copyright file="RouteUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import {
  chain,
  flatten,
  get,
  getOr,
  isEmpty,
  isEqual,
  isNil,
  join,
  lowerCase,
  omit,
  partial,
  startsWith
} from 'lodash/fp';
import ensure from './Izenda.Ensure';

const AppGlobal = require('Izenda.AppGlobal');
const config = require('Izenda.Config');
const queryString = require('query-string');
const ReportDesignerConstants = require('logic/data/Izenda.ReportDesignerConstants');
const subReportLink = require('share/utils/Izenda.SubReportLink');
const uiState = require('logic/Izenda.UiState');
const customURLConstants = require('logic/data/Izenda.PropertiesBuilderConstants').CUSTOM_URL;
const SubReportUtils = require('share/utils/Izenda.SubReportUtils');
const SubReportLink = require('share/utils/Izenda.SubReportLink');

require('share/utils/Izenda.UrlMonitor');

const PREFIX_FILTER_KEY = 'p';

/**
 * Get configured route by name and path
 * @param {Array} args
 * @example getConfigRoute('Dashboard', 'List')
 */
function getConfigRoute(...args: Array<any>) {
  if (isEmpty(args)) {
    return config.RoutesMerge;
  }

  const routesParams = flatten(args);
  const routePath = join('.children.', routesParams);

  return get(routePath, config.RoutesMerge);
}

/**
 * Get url for specific component
 * @param  {Object}    urlObject Url pattern
 * @param  {Object}    params   Params object to replace in URL pattern
 */
function getRouteUrl(urlObject: Object, params: Object = {}): string {
  const { BaseUrl } = config;
  const buildUrl = urlNode => {
    if (!urlNode) {
      return '';
    }
    return `${buildUrl(urlNode.parent)}/${urlNode.path}`;
  };

  let subRoute = buildUrl(urlObject);
  // replace react-router params url using pattern :param
  let url = Object.keys(params).reduce((route, key) => {
    let pattern = new RegExp(`\\(?\/?:${key}\\)?`, 'i');
    let matched = pattern.exec(subRoute);
    let paramValue = matched && matched[0].indexOf('/') >= 0 ? `/${params[key]}` : params[key];
    return route.replace(pattern, paramValue);
  }, subRoute);

  const resolvedUrl = url.replace(/\((.*?)\)?\)/, '').replace(')', '');

  return String.concatToUrl([BaseUrl, resolvedUrl]);
}

/**
 * Resolve url base on route configuration
 * @param {Array<string>} componentRoute Route name
 * @param {string} child Sub route name
 * @param {Object} params Route params
 */
function getUrl(componentRoute: Array<string>, child: string, params: any): string {
  const rootComponentRoute = get(componentRoute, getConfigRoute());

  ensure(rootComponentRoute, 'must have component route in Route Constants');
  const childrenRoute = getOr(rootComponentRoute, 'children.' + child, rootComponentRoute);

  return getRouteUrl(childrenRoute, params);
}

/**
 * Get NoPermission url
 */
function getNoPermissionUrl() {
  return `${config.BaseUrl}/NoPermission`.replace('//', '/');
}

const partialGetUrl = partial(getUrl);

const getSettingsUrl = partialGetUrl(['Settings']);

const getDashboardUrl = partialGetUrl(['Dashboard']);

const getReportUrl = partialGetUrl(['Report']);

const getExploreUrl = partialGetUrl(['Explore']);

const getReportDesignerUrl = partialGetUrl(['ReportDesigner']);

const getViewerUrl = partialGetUrl(['Viewer']);

const getReportViewerUrl = partialGetUrl(['ReportViewer']);

const getMyProfileUrl = partialGetUrl(['MyProfile']);

/**
 * Transform query object to filter values
 * @param  {Object} query Query object in window.location
 * @example
 * Input: ?p1=123&p2=456%3B%23789
 * Output: [{positionString:"1",value:"123"}, {positionString:"2",value:"456;#789"}]
 */
function transformQueryToFilters(query: Object): any {
  const isPrefixedFilter = key => startsWith(PREFIX_FILTER_KEY, lowerCase(key));
  const toFilter = filterKey => ({
    positionString: filterKey.substring(1),
    value: decodeURIComponent(query[filterKey])
  });

  return chain(query)
    .keys()
    .filter(isPrefixedFilter)
    .map(toFilter)
    .value();
}

/**
 * Resolve and navigate to sub report url
 * @param {Object} props
 * @param {Object} subReport
 * @param {Array<Object>} record
 * @param {Object} fieldConfig
 */
function openSubReport(props: Object, subReport: Object, record: Array<Object>, fieldConfig: Object) {
  var reportPart = props.reportPart;
  var filterInfo = SubReportUtils.buildFilterDataCommon(record, subReport.mappingFields, reportPart);

  if (!filterInfo.success) {
    return props.confirmMessage('error', filterInfo.errors);
  }
  var filterInfoForSubReport = props.getFilterInfoForSubReport();
  var subReportId = subReport.selectedReport;

  if (subReport.style === ReportDesignerConstants.DRIL_DOWN_STYLE.POPUP) {
    var filterData = SubReportUtils.buildFilterDataForEmbededReportPart(
      filterInfo.fieldMapping,
      reportPart,
      subReport,
      filterInfoForSubReport
    );

    // ReportViewerPopup is declared here to avoid circula dependency
    var ReportViewerPopup = require('modules/Reports/ReportViewer/Izenda.ReportViewerPopup')();

    return props.openPopup(ReportViewerPopup, {
      reportId: subReportId,
      queryObject: filterData
    });
  }

  var urlData = SubReportUtils.buildFilterDataForURL(
    filterInfo.fieldMapping,
    reportPart,
    fieldConfig.fieldElement,
    subReport,
    filterInfoForSubReport
  );

  if (!urlData.success) {
    return props.confirmMessage('error', urlData.errors);
  }

  var subreportParams = SubReportLink.convertFilterToQuery(omit(['success'], urlData)) || {};
  var isIframeExported = checkIframeExported();
  if(isIframeExported) {
    var params = queryString.parse(window.location.search);
    subreportParams.token = get('token', params);
  }
  var paramsText = $izenda.param(subreportParams);
  var route = isIframeExported ? 'IframeExport' : 'View';

  var link =
    getReportUrl(route, {
      id: subReportId
    }) +
    '?' +
    paramsText;

  if (subReport.style === ReportDesignerConstants.DRIL_DOWN_STYLE.LINK) {
    const href = window.location.href;
    const isReportViewerMode = href.indexOf('/view/') > -1;
    if (uiState.isDirty && !isReportViewerMode) {
      return props.confirmMessage('info', ReportDesignerConstants.BUNDLE.MESSAGE.OPEN_SUB_REPORT_WARNING);
    } else {
      if (props.router) {
        props.router.push(link);
      } else {
        window.location.href = link;
      }
      return null;
    }
  }

  window.open(makeAbsoluteUrl(link, null, true), '_blank');
}

/**
 * Resolve and navigate to custom field url of a report
 * @param {*} props
 * @param {*} param1
 * @param {*} mappingDataValues
 * @param {*} reportId Opening report identifier
 * @param {*} isExternal Open external url or internal app url
 * @param {*} fieldConfig
 */
function openCustomURL(
  { confirmDirtyForm, router }: Object,
  { url, option }: Object,
  mappingDataValues: Array<Object>,
  reportId: string,
  isExternal: boolean,
  fieldConfig: Object
) {
  const link = isExternal
    ? getCustomURLExternalLink(url, mappingDataValues, fieldConfig)
    : getCustomURLInternalLink(mappingDataValues, reportId);

  const { innerWidth, innerHeight } = window;

  switch (option) {
    case customURLConstants.OPTIONS.LINK_CURRENT_WINDOW:
      confirmDirtyForm(uiState.isDirty, {
        onConfirmDirtyForm: uiState.onOkDirtyForm
      }).then(() => {
        if (isExternal) {
          window.open(link, '_top');
        } else {
          if (router) {
            router.push(link);
          } else {
            window.location.href = link;
          }
        }
      });
      break;
    case customURLConstants.OPTIONS.LINK_NEW_WINDOW:
      window.open(
        isExternal ? link : makeAbsoluteUrl(link, null, true), // handle url for integrated mode
        '_blank',
        `toolbar=1,location=1,menubar=1,statusbar=1,width=${innerWidth},height=${innerHeight}`
      );
      break;
    case customURLConstants.OPTIONS.LINK_NEW_TAB:
      // handle url for integrated mode
      window.open(isExternal ? link : makeAbsoluteUrl(link, null, true));
      break;
  }
  return null;
}

/**
 * Generate external custom url of a report
 * @param {string} url
 * @param {Array<Object>} mappingDataValues
 * @param {Object} fieldConfig
 */
function getCustomURLExternalLink(url: string, mappingDataValues: Array<Object>, fieldConfig: Object): string {
  const fieldNameAlias = get('fieldElement.fieldNameAlias', fieldConfig);
  const encodeVariablesInCustomUrl = AppGlobal.advancedSettings.other.encodeVariablesInCustomUrlValue;

  mappingDataValues.forEach(mapping => {
    let mappingValue = encodeVariablesInCustomUrl ? encodeURIComponent(mapping.value) : mapping.value;

    url = url.replace('{[' + mapping.fieldNameAlias + ']}', mappingValue);
    if (mapping.fieldNameAlias === fieldNameAlias) {
      url = url.replace('{0}', mappingValue);
    }
  });
  return url;
}

/**
 * Generate internal custom url of a report
 * @param {Array<Object>} mappingDataValues
 * @param {string} reportId
 */
function getCustomURLInternalLink(mappingDataValues: Array<Object>, reportId: string): string {
  var baseLink = getReportUrl('View', {
    id: reportId
  });

  return baseLink + subReportLink.buildSubReportQuery(mappingDataValues);
}

/**
 * Convert relative URL to absolute URL appending query string params
 * @param {string} relativeUrl
 * @param {string} queryStringParams
 */
function makeAbsoluteUrl(relativeUrl: string = '', queryStringParams: string = '', forceSimpleMode: boolean = false): string {
  const sourceRegex = /\?source=[^&]+&?/i;
  const hashRegex = /^#/;
  const slashRegex = /^\//;
  const endSlashRegext = /\/$/;

  const location = window.location;
  let hash = decodeURIComponent(location.hash);
  let { origin, pathname, search, protocol, hostname, port } = location;

  if (!origin || origin === 'null') {
    origin = `${protocol}//${hostname + (port ? ':' + port : port)}`;
  }

  const queryString = queryStringParams ? (relativeUrl.indexOf('?') > -1 ? '&' : '?') + queryStringParams : '';
  const slash = origin.match(endSlashRegext) ? '' : '/';
  let absUrl = `${origin}${slash}${relativeUrl.replace(slashRegex, '')}${queryString}`;

  // forceSimpleMode: in the case of subreports, we need to use direct links, as is done when exporting to pdf, for example
  if (config.Integration && !forceSimpleMode) {
    // Sanitize the hash of request to remove "source" and extra "&"
    // For example: ?source=abc&p2=2&p3=3 --> #...?p2=2&p3=3
    let currentPath = hash ? hash.replace(sourceRegex, '') : '';

    // Construct url: https://developer.mozilla.org/en-US/docs/Learn/Common_questions/What_is_a_URL
    const hashParam = currentPath.replace(hashRegex, '');
    let absHostRoutingUrl;
    if (search.toLowerCase().indexOf(hashParam.toLowerCase()) >= 0) {
      absHostRoutingUrl = `${origin}${pathname}${search}#`;
    } else {
      absHostRoutingUrl = `${origin}${pathname}${search}#${hashParam}`;
    }

    let izendaSourceQuery = `${relativeUrl}${queryStringParams ? '?' + queryStringParams : ''}`;

    const questionMark = currentPath.indexOf('?') > -1 ? '' : '?';

    absUrl = `${absHostRoutingUrl}${questionMark}source=${izendaSourceQuery}`;
  }

  return absUrl;
}

/**
 * Sync in-memory location with browser address bar
 * @param {Object} location
 */
function syncLocation(location: Object, query: any) {
  if (isNil(location)) return;

  !isNil(query) && (location.query = query);
  var queryString = $izenda.param(location.query);
  var path = location.pathname + (queryString ? '?' + queryString : '');

  saveIzendaRoutingPath(path);
}

/**
 * Listen to history changed and sync hash of location
 * @param {Object} history
 */
function listenHistoryChange({ synergyHistory, isPageLoad }, path: string, query: any) {
  let isFirstLoad = true;

  // Do extra setup works on when embedded in customer frontend app
  UrlHashMonitor.init();
  synergyHistory.listenBefore(location => {
    syncLocation(location, isFirstLoad ? query : null);
  });

  if ('onhashchange' in window) {
    window.onhashchange = event  => {
      removeListener();
      synergyHistory.push(getHashLocation());
    };
  }

  // Navigate to specific page
  navigateToSpecificPage(synergyHistory, isPageLoad, path);

  var removeListener = synergyHistory.listen(event => {
    // Sync in-memory location with browser address bar
    syncLocation(event, isFirstLoad ? query : null);
    isFirstLoad = false;
  });
}

/**
 * Navigate to specific page
 * @param {Object} history
 * @param {Boolean} isPageLoad
 * @param {Object} path
 */
function navigateToSpecificPage(history: any, isPageLoad: boolean, path: string) {
  if (isPageLoad) {
    const izendaPathFromSource = getHashLocation();
    if (isEqual(izendaPathFromSource, config.BaseUrl)) {
      history.push(path);
    } else {
      history.push(izendaPathFromSource);
    }
  } else {
    history.push(path);
  }
}

// parse hash string to location object
function getHashLocation() {
  return getIzendaRoutingPath(config.BaseUrl);
}

/**
 * get the previous link
 *   when in integration mode then get hash location
 *   when in stand alone then get the path name
 */
function getPreviousLink() {
  if (config.Integration) {
    return getHashLocation();
  }
  return window.location.pathname;
}

/**
 * get the path for integration mode
 */
function getPathForIntegration(path){
  return getRoutingPath(path);
}

function checkIframeExported() {
  const href = window.location.href;
  const isIframeRoute = href.indexOf('/iframe/') > -1;

  const params = queryString.parse(window.location.search);
  const isTokenExist = !isNil(get('token', params));

  return isIframeRoute && isTokenExist;
}

/* PRIVATE FUNCTION */
function saveIzendaRoutingPath(path) {
  // TODO: Handle case location.hash has mutiple query params
  // (added either by customer frontend app or by ourself)
  let oldPath = decodeURIComponent(location.hash);
  let newPath = decodeURIComponent(getRoutingPath(path));
  if (oldPath === newPath)
    return;
  location.hash = getRoutingPath(path);
}

function getRoutingPath(path) {
  if (!isSourceParamApplied(path))
    return getCurrentPath() + '?source=' + path;
  else
    return path;
}

function isSourceParamApplied(path) {
  return path && path.match(/^(.+?)\?source=?/g);
}

function getCurrentPath() {
  // We keep the izenda path into location.hash (no impact to routing) so that
  // the corresponding page can be accessed later on by bookmarked url
  const decodedHash = decodeURIComponent(location.hash);
  const match = /^(.+?)\?source=?/g.exec(decodedHash);
  return match ? match[1] : decodedHash;
}

function getIzendaRoutingPath(defaultPath) {
  const match = /\?source=?(.+)/g.exec(location.hash);
  let path = defaultPath;

  if (match) {
    const matchHashUrl = match[1];
    path = matchHashUrl || defaultPath;
  }

  return path;
}
/* END PRIVATE FUNCTION */

export default {
  getUrl,
  getSettingsUrl,
  getDashboardUrl,
  getMyProfileUrl,
  getReportUrl,
  getExploreUrl,
  getReportDesignerUrl,
  getViewerUrl,
  getReportViewerUrl,
  getConfigRoute,
  getRouteUrl,
  openSubReport,
  openCustomURL,
  getCustomURLExternalLink,
  getCustomURLInternalLink,
  transformQueryToFilters,
  makeAbsoluteUrl,
  listenHistoryChange,
  getHashLocation,
  getPreviousLink,
  getNoPermissionUrl,
  getPathForIntegration,
  checkIframeExported
};



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.RouteUtils.js